import {
  getMarketPriceSettingLabel,
  MARKET_PRICE_SETTING_OPTIONS,
} from "constants/marketPriceSetting";
import { sizer } from "layout/styles/styled/sizer";
import { debounce } from "lodash";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetMarketPriceDatasetById,
  useGetMarketPrices,
} from "store/queries/marketPrices";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { IMarketPrice, MarketPriceSetting } from "types/marketPrice";

const PageContainer = styled.div`
  padding: ${sizer(4)};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${sizer(3)};
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: ${sizer(2)};
  align-items: center;
`;

const ProviderInfo = styled.div`
  margin-bottom: ${sizer(3)};
  color: #6c757d;
  font-size: 0.95rem;

  strong {
    color: #212529;
  }
`;

const SearchSection = styled.div`
  display: flex;
  gap: ${sizer(2)};
  margin-bottom: ${sizer(3)};
`;

const PriceCell = styled.span`
  font-weight: 600;
  color: #28a745;
`;

export function MarketPricesView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [globalFilter, setGlobalFilter] = useState("");
  const [competitorFilter, setCompetitorFilter] = useState("");
  const [cptFilter, setCptFilter] = useState("");
  const [settingFilter, setSettingFilter] = useState<MarketPriceSetting | null>(
    null
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 25,
    page: 0,
    sortField: null as string | null,
    sortOrder: null as -1 | 1 | null,
  });

  // Debounce the search input
  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearch(globalFilter);
    }, 300);

    handler();
    return () => handler.cancel();
  }, [globalFilter]);

  const { data: dataset, isLoading: isLoadingDataset } =
    useGetMarketPriceDatasetById(id || "");

  // Convert sort order for API
  let sortOrder: "asc" | "desc" | undefined;
  if (lazyParams.sortOrder === 1) {
    sortOrder = "asc";
  } else if (lazyParams.sortOrder === -1) {
    sortOrder = "desc";
  }

  const { data: pricesData, isLoading: isLoadingPrices } = useGetMarketPrices({
    datasetId: id || "",
    competitorName: competitorFilter || undefined,
    cptCode: cptFilter || undefined,
    setting: settingFilter || undefined,
    search: debouncedSearch || undefined,
    limit: lazyParams.rows,
    offset: lazyParams.first,
    sortField: lazyParams.sortField || undefined,
    sortOrder,
  });
  const { data: providers } = useGetAllProvidersQuery();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const priceBodyTemplate = (rowData: IMarketPrice) => {
    return <PriceCell>{formatCurrency(rowData.rate)}</PriceCell>;
  };

  const settingBodyTemplate = (rowData: IMarketPrice) => {
    return getMarketPriceSettingLabel(rowData.setting) || "—";
  };

  const payerBodyTemplate = (rowData: IMarketPrice) => {
    return rowData.payer?.name || "Unknown";
  };

  const providerBodyTemplate = (rowData: IMarketPrice) => {
    return rowData.provider?.name || "Unknown";
  };

  const getProviderNames = () => {
    if (!dataset?.providerIds || !providers?.data) {
      return "N/A";
    }

    const providerNames = dataset.providerIds
      .map((id) => providers.data.find((p: any) => p.id === id)?.name)
      .filter(Boolean);

    if (providerNames.length === 0) return "N/A";
    return providerNames.join(", ");
  };

  const handleBack = () => {
    navigate(`/payer-rules?tab=4`);
  };

  const handleEdit = () => {
    navigate(`/payer-rules/market-prices/edit/${id}`);
  };

  const onSort = (event: any) => {
    setLazyParams(event);
  };

  const onPage = (event: any) => {
    setLazyParams(event);
  };

  if (isLoadingDataset) {
    return (
      <PageContainer>
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      </PageContainer>
    );
  }

  if (!dataset) {
    return (
      <PageContainer>
        <Card>
          <p>Dataset not found</p>
          <Button label="Back to Market Prices" onClick={handleBack} />
        </Card>
      </PageContainer>
    );
  }

  const title = (
    <HeaderSection>
      <div>
        <h2 style={{ margin: 0 }}>{dataset.displayName}</h2>
        {dataset.description && (
          <p style={{ margin: "0.5rem 0 0 0", color: "#6c757d" }}>
            {dataset.description}
          </p>
        )}
      </div>
      <HeaderButtons>
        <Button
          label="Edit"
          icon="pi pi-pencil"
          className="p-button-outlined"
          onClick={handleEdit}
        />
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-text"
          onClick={handleBack}
        />
      </HeaderButtons>
    </HeaderSection>
  );

  return (
    <PageContainer>
      <Card title={title}>
        <ProviderInfo>
          <strong>Provider(s):</strong> {getProviderNames()}
        </ProviderInfo>

        <SearchSection>
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search all fields..."
              style={{ width: "300px" }}
            />
          </span>
          <InputText
            value={competitorFilter}
            onChange={(e) => setCompetitorFilter(e.target.value)}
            placeholder="Filter by competitor..."
          />
          <InputText
            value={cptFilter}
            onChange={(e) => setCptFilter(e.target.value)}
            placeholder="Filter by CPT code..."
          />
          <Dropdown
            value={settingFilter}
            options={MARKET_PRICE_SETTING_OPTIONS}
            onChange={(e) => {
              setSettingFilter(e.value ?? null);
              setLazyParams((prev) => ({ ...prev, first: 0, page: 0 }));
            }}
            placeholder="All settings"
            showClear
            style={{ minWidth: "200px" }}
          />
        </SearchSection>

        <DataTable
          value={pricesData?.data || []}
          loading={isLoadingPrices}
          lazy
          paginator
          rows={lazyParams.rows}
          totalRecords={pricesData?.total || 0}
          first={lazyParams.first}
          onSort={onSort}
          onPage={onPage}
          sortField={lazyParams.sortField || undefined}
          sortOrder={lazyParams.sortOrder || undefined}
          rowsPerPageOptions={[25, 50, 100]}
          emptyMessage="No market prices found"
          className="datatable-responsive"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} prices"
          rowHover
          stripedRows
        >
          <Column
            field="competitorName"
            header="Competitor"
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="provider.name"
            header="Provider"
            body={providerBodyTemplate}
            sortable
            style={{ width: "17%" }}
          />
          <Column
            field="payer.name"
            header="Payer"
            body={payerBodyTemplate}
            sortable
            style={{ width: "20%" }}
          />
          <Column
            field="setting"
            header="Setting"
            body={settingBodyTemplate}
            style={{ width: "13%" }}
          />
          <Column
            field="cptCode"
            header="CPT Code"
            sortable
            style={{ width: "12%" }}
          />
          <Column
            field="rate"
            header="Rate"
            body={priceBodyTemplate}
            sortable
            style={{ width: "18%" }}
          />
        </DataTable>
      </Card>
    </PageContainer>
  );
}

export default MarketPricesView;
